import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { BookOpen } from 'lucide-react';
import MultipleChoiceWidget from '../widgets/MultipleChoiceWidget';
import TrueFalseWidget from '../widgets/TrueFalseWidget';

/**
 * CeAssessmentQuestion - Wrapper for existing widgets to display CE questions
 * 
 * Pixel-perfect port from Polymet prototype with succession theme variables.
 * Reuses existing Plan Builder widgets for UI consistency.
 */
const CeAssessmentQuestion = ({
  questionNumber,
  totalQuestions,
  questionText,
  questionType,
  options = [],
  hintLink,
  learningObjective,
  correctAnswerText,
  onAnswer,
  disabled = false,
  className = '',
}) => {
  // Transform options for multiple choice widget
  // Options from GraphQL come as { id, text } - we need { label, value }
  const normalizedOptions = options.map(opt => ({
    label: opt.text || opt,
    value: opt.id || opt.text || opt,
  }));

  const intl = useIntl();

  // Handle answer from widget and transform to option ID
  const handleAnswer = (answer) => {
    // MultipleChoiceWidget returns { value: optionId }
    // TrueFalseWidget returns { value: 'true' } or { value: 'false' }
    // For true/false, we need to map back to the actual option ID
    if (questionType === 'true_false') {
      const selectedText = answer.value === 'true' ? 'True' : 'False';
      const selectedOption = options.find(
        opt => opt.text?.toLowerCase() === selectedText.toLowerCase()
      );
      onAnswer(selectedOption?.id || answer.value);
    } else {
      onAnswer(answer.value);
    }
  };

  return (
    <div
      className={`
        border border-succession-border rounded-xl bg-succession-bg-primary
        transition-all duration-300
        ${className}
      `}
    >
      {/* Inner padding: p-8 (32px) */}
      <div className="p-8 space-y-6">
        {/* Question Header */}
        <div className="space-y-3">
          {/* Top row: flex items-center justify-between */}
          <div className="flex items-center justify-between">
            {/* Question number: text-sm font-medium text-primary */}
            <span className="text-sm font-medium text-succession-primary">
              {intl.formatMessage({ id: 'succession.plans.ce_progress_question_of' }, { current: questionNumber, total: totalQuestions })}
            </span>
            {/* Learning objective badge: text-xs text-muted-foreground bg-muted px-2 py-1 rounded */}
            {learningObjective && (
              <span className="text-xs text-succession-text-secondary bg-succession-bg-secondary px-2 py-1 rounded">
                {learningObjective.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          {/* Question text: text-xl font-semibold leading-relaxed */}
          <h3 className="text-xl font-semibold leading-relaxed text-succession-text-primary">
            {questionText}
          </h3>

          {/* Admin-only correct answer hint for testing */}
          {correctAnswerText && (
            <p className="text-sm text-green-600 italic mt-2">
              {intl.formatMessage({ id: 'succession.plans.ce_correct_answer_hint' }, { answer: correctAnswerText })}
            </p>
          )}
        </div>

        {/* Widget container: pt-2 (8px top padding) */}
        {/* Key prop forces remount when question changes, resetting widget internal state */}
        <div className="pt-2">
          {questionType === 'multiple_choice' && (
            <MultipleChoiceWidget
              key={`mc-${questionNumber}`}
              options={normalizedOptions}
              onAnswer={handleAnswer}
              disabled={disabled}
            />
          )}

          {questionType === 'true_false' && (
            <TrueFalseWidget
              key={`tf-${questionNumber}`}
              onAnswer={handleAnswer}
              disabled={disabled}
            />
          )}
        </div>

        {/* Hint Link: pt-4 border-t border-border */}
        {hintLink && (
          <div className="pt-4 border-t border-succession-border">
            <button
              type="button"
              className="
                inline-flex items-center gap-2 px-3 py-2
                text-sm text-succession-text-secondary 
                hover:text-succession-text-primary hover:bg-succession-hover-bg
                rounded-lg transition-all duration-300
              "
              onClick={() => window.open(hintLink.url, '_blank')}
            >
              <BookOpen className="w-4 h-4" />
              {hintLink.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

CeAssessmentQuestion.propTypes = {
  questionNumber: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  questionText: PropTypes.string.isRequired,
  questionType: PropTypes.oneOf(['multiple_choice', 'true_false']).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
      }),
    ])
  ),
  hintLink: PropTypes.shape({
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  learningObjective: PropTypes.string,
  correctAnswerText: PropTypes.string,
  onAnswer: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

CeAssessmentQuestion.defaultProps = {
  options: [],
  hintLink: null,
  learningObjective: null,
  correctAnswerText: null,
  disabled: false,
  className: '',
};

export default CeAssessmentQuestion;

